import { useFormContext, useWatch } from 'react-hook-form'
import { useSelector } from 'react-redux'

import { Card } from '@/components/Elements'
import { authSelector } from '@/stores/auth/authSlice'
import { Badge, HStack, SimpleGrid } from '@chakra-ui/react'
import React, { useEffect } from 'react'
import { toast } from 'react-toastify'
import AddCustomerModal from '../../Modal/AddCustomerModal'

import SingleSelect from '@/components/Form/Select/SingleSelect'

import { DatePicker, Input, Select } from '@/components/Form'

//API
import useUser from '@/features/users/useUser'

//Constant
import { ContributorGroupType } from '@/features/contributorGroups/constants'
import { useCustomer } from '@/features/customers/useCustomer'
import { offerQuotationGroupPolicyType } from '@/features/offers/constants/offerEditRights/offerQuotationEditRights'
import useOffer from '@/features/offers/hooks/useOffer'
import { selectOfferDetail, selectOfferPermissions } from '@/features/offers/slices/offerDetail.slice'
import { SystemVariableType } from '@/features/systemVariables/constants'
import useSystemVariable from '@/features/systemVariables/useSystemVariable'
import { useAddCustomerModalManagement } from '@/stores/offers/addCustomerModalSlice'

const OfferOverviewGeneralInfo = () => {
  const { adminMode } = useSelector(selectOfferDetail)
  const { user } = useSelector(authSelector)
  const offerPermissions = useSelector(selectOfferPermissions)

  const { getValues, setValue } = useFormContext()
  const { open: openAddCustomerModal } = useAddCustomerModalManagement()

  //Watch
  const subSystems = useWatch({ name: 'subSystems' })
  const revision = useWatch({ name: 'revision' })
  const customer = useWatch({ name: 'customer' })
  const offerId = useWatch({ name: '_id' })
  const creator = useWatch({ name: 'creator' })
  const createdBy = useWatch({ name: 'createdBy' })
  const offerStatus = useWatch({ name: 'status' })

  const { useFetchAllCustomers, useCreateTemporaryCustomer, useUpdateCustomer, useCustomerOptions } = useCustomer()
  const { usePatchOffer } = useOffer()

  const { useFetchAllUsers, useUserOptions } = useUser()
  const { useSystemVariableOptions } = useSystemVariable()

  const updateCustomerMutation = useUpdateCustomer()
  const createTemporaryCustomer = useCreateTemporaryCustomer()
  const patchOffer = usePatchOffer()

  const { data: fetchCustomerResponse, refetch: refetchCustomerOptions } = useFetchAllCustomers({
    page: 1,
    pageSize: 0,
  })

  //Translated
  const hasHighRisk = subSystems.filter((subsystem) => subsystem.isHighRisk).length > 0
  const canEditGeneral = offerPermissions[offerQuotationGroupPolicyType.QUOTATION_OFFER_OVERVIEW]?.edit
  const canEditSaleSideData = offerPermissions[offerQuotationGroupPolicyType.QUOTATION_OFFER_OVERVIEW_SALES_SIDE]?.edit

  const { data: creatorOptions } = useUserOptions({
    labelKey: 'fullName',
    valueKey: '_id',
  })
  const { data: salesUserOptions } = useUserOptions({
    labelKey: 'fullName',
    valueKey: '_id',
    contributorGroup: [
      ContributorGroupType.TECHNICAL_PROJECT_MANAGER,
      ContributorGroupType.SALES,
      ContributorGroupType.ENGINEER,
    ],
  })
  const { data: teamOptions } = useSystemVariableOptions(SystemVariableType.TEAM, { status: 'active', valueKey: '_id' })
  const { data: businessUnitOptions } = useSystemVariableOptions(SystemVariableType.BUSINESS_UNIT, {
    status: 'active',
    valueKey: '_id',
  })

  const { data: allCustomerOptions } = useCustomerOptions({
    labelKey: (customer) => `${customer?.code ?? '(No Code)'}-${customer?.name}`,
    status: ['active', 'inactive', 'temporary', 'listed'],
  })

  const { data: activeCustomerOptions } = useCustomerOptions({
    labelKey: (customer) => `${customer?.code ?? '(No Code)'}-${customer?.name}`,
    status: ['active', 'listed'],
  })

  useEffect(() => {
    const handleUpdateCustomerDetail = async ({ customerName: originalCustomerName, BRNo: originalBRNo }) => {
      const { isSubmitted, customerName, BRNo } = await openAddCustomerModal({
        title: 'Update Customer Detail',
        customerName: originalCustomerName,
        BRNo: originalBRNo,
      })

      if (!isSubmitted) {
        return
      }
      try {
        const result = await updateCustomerMutation.mutateAsync({
          id: customer,
          data: {
            name: customerName,
            businessRegistrationNumber: BRNo,
            isDeleted: false,
          },
        })

        refetchCustomerOptions()

        setValue(`customer`, result._id, { shouldDirty: true })
      } catch (error) {
        console.error(error)
      }
    }

    if (!fetchCustomerResponse?.data || !customer) {
      return
    }

    const currentCustomer = fetchCustomerResponse?.data.find((item) => item._id == customer)

    if (!currentCustomer) {
      return
    }

    if (currentCustomer.isDeleted) {
      toast.error('This customer is inactive. Please ask Customer Admin')
      return
    }

    if (
      (!currentCustomer.businessRegistrationNumber || currentCustomer.businessRegistrationNumber === '') &&
      currentCustomer.isTemporary &&
      (user._id === creator || user._id === createdBy)
    ) {
      handleUpdateCustomerDetail({
        customerName: currentCustomer.name,
        BRNo: currentCustomer.businessRegistrationNumber,
      })
    }

    //eslint-disable-next-line
  }, [customer, fetchCustomerResponse])

  const handleCreateTempCustomer = async () => {
    const { isSubmitted, customerName, BRNo } = await openAddCustomerModal({
      title: 'Create Temp Customer',
    })

    if (!isSubmitted || customerName === '') {
      return
    }
    try {
      const result = await createTemporaryCustomer.mutateAsync({
        name: customerName,
        businessRegistrationNumber: BRNo,
      })

      console.log(result)

      const newCustomerId = result?.data?._id
      if (newCustomerId) {
        await patchOffer.mutateAsync({
          offerId: offerId,
          payload: [{ op: 'replace', path: `customer`, value: newCustomerId }],
        })
        setValue(`customer`, newCustomerId, { shouldDirty: true })
      }
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <>
      <AddCustomerModal />
      <Card
        title={'General Info'}
        hasBorder={false}
        canCollapse
        rightElement={<HStack>{hasHighRisk && <Badge colorScheme="red">High Risk Subsystems</Badge>}</HStack>}
      >
        <SimpleGrid columns={[2, null, 2]} spacingX="40px" spacingY="15px">
          <Input
            name="quotationNumber"
            label="Quotation Number"
            type="text"
            placeholder=""
            disabled
            isRequired={false}
            isRow
          />

          {adminMode && (
            <Input name="baseQuotationNumber" label="Base Quotation Number" type="text" isRequired={false} isRow />
          )}

          <Input
            name="revision"
            type="number"
            label="Version"
            value={revision}
            isRequired={false}
            isRow
            disabled={!adminMode}
          />
          <Input name="projectName" label="Project Name" disabled={!canEditGeneral} isRow />
          <Select
            name="businessUnit"
            label="Business Unit"
            disabled={!canEditGeneral}
            options={businessUnitOptions}
            isRow
          />
          <SingleSelect
            name="customer"
            label="Customer"
            disabled={!canEditGeneral}
            options={activeCustomerOptions}
            allOptions={allCustomerOptions}
            isRow
            showButton={canEditGeneral}
            buttonText={'Add'}
            buttonFunction={handleCreateTempCustomer}
          />

          <Select name="department" label="Team" disabled={!canEditGeneral} options={teamOptions} isRow />
          <Select
            name="countryOfInstallation"
            label="Country of Installation / Sales Office"
            disabled={!canEditGeneral}
            options={[
              { value: 'hk', label: 'Hong Kong' },
              { value: 'mo', label: 'Macau' },
              { value: 'others', label: 'CN / Overseas' },
            ]}
            isRow
          />
          <Select
            name="createdBy"
            options={salesUserOptions}
            label="Sales Responsible"
            isRequired
            isRow
            disabled={!adminMode}
          />
          <DatePicker name="createdAt" label="Create Date" disabled={!adminMode} isRow isRequired={false} />
          <Select
            name="creator"
            options={creatorOptions}
            label="Creator"
            isRequired={false}
            isRow
            disabled={!adminMode}
          />
          <Input name="site" label="Site" isRow isRequired={false} disabled={!canEditSaleSideData} />
          <Input
            name="customerReference"
            label="Customer Ref."
            isRow
            isRequired={false}
            disabled={!canEditSaleSideData}
          />
        </SimpleGrid>
      </Card>
    </>
  )
}

export default OfferOverviewGeneralInfo
